/**
 * Session Management with Idle Timeout
 * Handles user session timeout and provides warnings before automatic logout
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { Modal, Button, Progress, Typography, Space } from 'antd';
import { ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../AuthContext';
import { IdleTimerConfig, AUTH_STORAGE_KEYS } from '../../Types/auth';

const { Text, Title } = Typography;

// Configuration constants
const TIMEOUT_LIMIT = 30 * 60 * 1000; // 30 minutes
const WARNING_TIME = 10 * 60 * 1000;  // 10 minutes before timeout
const CHECK_INTERVAL = 1000;          // 1 second

const defaultConfig: IdleTimerConfig = {
  timeout: TIMEOUT_LIMIT,
  warningTime: WARNING_TIME,
  crossTab: false  // Disable cross-tab sync to avoid BroadcastChannel errors
};

interface IdleSessionManagerProps {
  children: React.ReactNode;
  config?: Partial<IdleTimerConfig>;
  onWarning?: () => void;
  onTimeout?: () => void;
}

/**
 * Format milliseconds into MM:SS format
 */
const formatTime = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Calculate progress percentage for the warning dialog
 */
const calculateProgress = (remaining: number, total: number): number => {
  return Math.max(0, Math.min(100, ((total - remaining) / total) * 100));
};

/**
 * Main session management component
 * Wraps the app and handles idle timeout logic
 */
export const IdleSessionManager: React.FC<IdleSessionManagerProps> = ({
  children,
  config = {},
  onWarning,
  onTimeout
}) => {
  const { signOut, isAuthenticated } = useAuth();
  const finalConfig = { ...defaultConfig, ...config };
  
  // State management
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [warningStartTime, setWarningStartTime] = useState(0);
  
  // Refs for managing intervals and preventing memory leaks
  const warningIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActiveRef = useRef<number>(Date.now());

  /**
   * Handle user becoming idle (timeout reached)
   */
  const handleOnIdle = useCallback((): void => {
    console.log('User session timed out due to inactivity');
    
    // Clear any warning timers
    if (warningIntervalRef.current) {
      clearInterval(warningIntervalRef.current);
    }
    
    // Call custom timeout handler if provided
    onTimeout?.();
    
    // Sign out the user
    signOut();
  }, [signOut, onTimeout]);

  /**
   * Handle user activity (reset idle state)
   */
  const handleOnAction = useCallback((): void => {
    lastActiveRef.current = Date.now();
    
    // Hide warning if it's showing
    if (showWarning) {
      setShowWarning(false);
      setRemainingTime(0);
      
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current);
        warningIntervalRef.current = null;
      }
    }
    
    // Store last active time for cross-tab support
    sessionStorage.setItem(AUTH_STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
  }, [showWarning]);

  /**
   * Initialize idle timer
   */
  const { getRemainingTime, getLastActiveTime, reset } = useIdleTimer({
    timeout: finalConfig.timeout,
    onIdle: handleOnIdle,
    onAction: handleOnAction,
    debounce: 500,
    crossTab: false  // Disable cross-tab to avoid BroadcastChannel errors
  });

  /**
   * Start the warning countdown
   */
  const startWarningCountdown = useCallback((startTime: number): void => {
    setWarningStartTime(startTime);
    setShowWarning(true);
    onWarning?.();
    
    // Start interval to update remaining time
    warningIntervalRef.current = setInterval(() => {
      const remaining = getRemainingTime();
      setRemainingTime(remaining);
      
      // If time runs out while warning is showing, the idle timer will handle logout
      if (remaining <= 0) {
        if (warningIntervalRef.current) {
          clearInterval(warningIntervalRef.current);
          warningIntervalRef.current = null;
        }
      }
    }, CHECK_INTERVAL);
  }, [getRemainingTime, onWarning]);

  /**
   * Check if we should show warning based on remaining time
   */
  useEffect(() => {
    if (!isAuthenticated) {
      // Clean up when not authenticated
      setShowWarning(false);
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current);
        warningIntervalRef.current = null;
      }
      return;
    }

    const checkWarningTime = (): void => {
      const remaining = getRemainingTime();
      
      // Show warning if we're within the warning period and not already showing
      if (remaining <= finalConfig.warningTime && remaining > 0 && !showWarning) {
        startWarningCountdown(Date.now());
      }
    };
    
    // Check immediately and then set up interval
    checkWarningTime();
    const interval = setInterval(checkWarningTime, CHECK_INTERVAL);
    
    return () => clearInterval(interval);
  }, [isAuthenticated, finalConfig.warningTime, showWarning, getRemainingTime, startWarningCountdown]);

  /**
   * Clean up on unmount
   */
  useEffect(() => {
    return () => {
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current);
      }
    };
  }, []);

  /**
   * Extend session by resetting the idle timer
   */
  const extendSession = useCallback((): void => {
    console.log('Extending user session');
    
    // Reset the idle timer
    reset();
    
    // Hide warning dialog
    setShowWarning(false);
    setRemainingTime(0);
    
    // Clear warning interval
    if (warningIntervalRef.current) {
      clearInterval(warningIntervalRef.current);
      warningIntervalRef.current = null;
    }
    
    // Update last active time
    handleOnAction();
  }, [reset, handleOnAction]);

  /**
   * End session immediately
   */
  const endSession = useCallback((): void => {
    console.log('User chose to end session');
    
    // Clear warning interval
    if (warningIntervalRef.current) {
      clearInterval(warningIntervalRef.current);
    }
    
    // Sign out
    signOut();
  }, [signOut]);

  // Calculate progress for the warning dialog
  const progress = remainingTime > 0 
    ? calculateProgress(remainingTime, finalConfig.warningTime)
    : 0;

  // Don't render session management for unauthenticated users
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <>
      {children}
      
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            Session Expiration Warning
          </Space>
        }
        open={showWarning}
        onCancel={extendSession}
        closable={true}
        maskClosable={false}
        keyboard={false}
        width={480}
        footer={[
          <Button 
            key="end" 
            onClick={endSession}
            style={{ marginRight: 8 }}
          >
            End Session
          </Button>,
          <Button 
            key="extend" 
            type="primary" 
            onClick={extendSession}
            icon={<ClockCircleOutlined />}
          >
            Yes, Keep Session Active
          </Button>
        ]}
        style={{ top: 100 }}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Title level={4} style={{ marginBottom: 16, color: '#fa8c16' }}>
            Your session will expire in
          </Title>
          
          <div style={{ 
            fontSize: 36, 
            fontWeight: 'bold', 
            color: remainingTime < 60000 ? '#ff4d4f' : '#fa8c16',
            marginBottom: 20,
            fontFamily: 'monospace'
          }}>
            {formatTime(remainingTime)}
          </div>
          
          <Progress
            percent={progress}
            strokeColor={{
              '0%': '#52c41a',
              '50%': '#faad14',
              '100%': '#ff4d4f',
            }}
            showInfo={false}
            style={{ marginBottom: 20 }}
          />
          
          <Text type="secondary" style={{ fontSize: 14 }}>
            Due to inactivity, your session will automatically expire.<br/>
            Do you want to keep this session alive?
          </Text>
          
          <div style={{ 
            marginTop: 16, 
            padding: 12, 
            background: '#f6ffed', 
            border: '1px solid #b7eb8f',
            borderRadius: 6 
          }}>
            <Text style={{ fontSize: 12, color: '#52c41a' }}>
              💡 <strong>Tip:</strong> Any mouse movement, keyboard input, or clicking will 
              automatically extend your session
            </Text>
          </div>
        </div>
      </Modal>
    </>
  );
};

/**
 * Hook for getting session information
 */
export const useSessionInfo = () => {
  const { isAuthenticated } = useAuth();
  
  const getLastActiveTime = (): number | null => {
    const stored = sessionStorage.getItem(AUTH_STORAGE_KEYS.LAST_ACTIVITY);
    return stored ? parseInt(stored, 10) : null;
  };
  
  const getSessionDuration = (): number => {
    const lastActive = getLastActiveTime();
    return lastActive ? Date.now() - lastActive : 0;
  };
  
  const isSessionActive = (): boolean => {
    return isAuthenticated && getSessionDuration() < TIMEOUT_LIMIT;
  };
  
  return {
    isAuthenticated,
    getLastActiveTime,
    getSessionDuration,
    isSessionActive,
    timeoutLimit: TIMEOUT_LIMIT,
    warningTime: WARNING_TIME
  };
};
